import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

type WarningRow = {
  id: string;
  client_id: string;
  title: string | null;
  slug: string | null;
  created_at: string;
  validation_errors: Array<{ code: string; field?: string; message?: string; severity: string }>;
  final_failure_reason: string | null;
  publish_status: string;
  client_name?: string;
};

export default function WarningsPanel() {
  const [rows, setRows] = useState<WarningRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResolved, setShowResolved] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: posts } = await supabase
      .from("blog_posts")
      .select("id, client_id, title, slug, created_at, validation_errors, final_failure_reason, publish_status")
      .order("created_at", { ascending: false })
      .limit(200);

    const filtered = (posts || []).filter((p: any) => {
      const errs = Array.isArray(p.validation_errors) ? p.validation_errors : [];
      return showResolved ? true : errs.length > 0 || !!p.final_failure_reason;
    });

    const clientIds = Array.from(new Set(filtered.map((p: any) => p.client_id)));
    const { data: clients } = clientIds.length
      ? await supabase.from("clients").select("id, name").in("id", clientIds as string[])
      : { data: [] as any[] };
    const nameMap = new Map((clients || []).map((c: any) => [c.id, c.name]));

    setRows(
      filtered.map((p: any) => ({
        ...p,
        client_name: nameMap.get(p.client_id) || "(unknown)",
      })) as WarningRow[],
    );
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showResolved]);

  const clearWarnings = async (id: string) => {
    const { error } = await supabase
      .from("blog_posts")
      .update({ validation_errors: [], final_failure_reason: null })
      .eq("id", id);
    if (error) { toast({ title: "Failed", description: error.message }); return; }
    toast({ title: "Warning cleared" });
    load();
  };

  return (
    <section className="mb-6 rounded-[14px] border border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Warnings / Needs Review</h2>
          <p className="text-xs text-muted-foreground">
            Non-critical issues surfaced by the validator. Articles still publish — these are for follow-up.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowResolved((v) => !v)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">No warnings.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const codes = (r.validation_errors || []).map((e) => e.code);
            return (
              <div key={r.id} className="rounded-[10px] border border-border bg-background p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.client_name} — {r.title || r.slug || r.id}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()} · {r.publish_status}
                    </div>
                    {codes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {codes.map((c, i) => (
                          <span
                            key={i}
                            className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                    {r.final_failure_reason && (
                      <div className="mt-2 text-xs text-amber-600 break-words">
                        {r.final_failure_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="outline" onClick={() => clearWarnings(r.id)}>
                      Ignore
                    </Button>
                    <Button size="sm" onClick={() => clearWarnings(r.id)}>
                      Resolve
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
